import { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import SignaturePadField from "../../components/SignaturePadField";
import {
  Check,
  ChevronRight,
  Loader2,
  Lock,
  Phone,
  Upload,
  Camera,
  FileText,
  Circle,
  CheckCircle2,
  ArrowLeft,
  CloudUpload,
  CreditCard,
  IdCard,
  HeartPulse,
  FileCheck,
  Download,
  ZoomIn,
  ZoomOut,
  Building2,
  Eye,
  RefreshCw,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import { Progress } from "../../components/ui/progress";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const FACILITY_PLACEHOLDER = "/images/dashboard-hero-1.png";

const STEPS = [
  { id: "welcome", label: "Benvenuto", progress: 10 },
  { id: "contratto", label: "Visiona contratto", progress: 20 },
  { id: "firma", label: "Firma contratto", progress: 40 },
  { id: "documenti", label: "Documenti richiesti", progress: 70 },
  { id: "terapia", label: "Terapia", progress: 85 },
  { id: "riepilogo", label: "Riepilogo", progress: 100 },
];

const DOC_ICONS = {
  ospite_id: IdCard,
  ospite_cf: CreditCard,
  ospite_tessera: HeartPulse,
  familiare_id: IdCard,
  familiare_cf: CreditCard,
  delega: FileCheck,
  analisi: FileText,
  terapia: FileText,
};

function formatApiDetail(detail, fallback = "Errore") {
  if (detail == null || detail === "") return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const parts = detail
      .map((item) => {
        if (typeof item === "string") return item;
        if (item && typeof item === "object") return item.msg || item.message || "";
        return "";
      })
      .filter(Boolean);
    return parts.join(". ") || fallback;
  }
  if (typeof detail === "object") return detail.msg || detail.message || fallback;
  return fallback;
}

function statusToStepIndex(status, checklist, therapyRequired, docsTotal = 0) {
  if (checklist?.submitted || status === "completed") return 5;
  if (status === "therapy_received") return 5;
  if (status === "documents_completed") {
    if (therapyRequired && !checklist?.terapia_ricevuta) return 4;
    return 5;
  }
  if (status === "documents_partial") return 3;
  if (status === "contract_signed") {
    if (docsTotal === 0 && !therapyRequired) return 5;
    return 3;
  }
  if (status === "contract_viewed") return 2;
  return 0;
}

function StrutturaLogo({ className = "w-10 h-10" }) {
  return (
    <svg viewBox="0 0 40 40" className={className} aria-hidden>
      <path d="M20 2 L38 20 L20 38 L2 20 Z" fill="#059669" opacity="0.9" />
      <path d="M20 8 L32 20 L20 32 L8 20 Z" fill="#2563eb" opacity="0.85" />
    </svg>
  );
}

function PortalHeader({ struttura, token }) {
  const [logoUrl, setLogoUrl] = useState(null);

  useEffect(() => {
    if (!struttura?.has_logo || !token) {
      setLogoUrl(null);
      return undefined;
    }

    let cancelled = false;
    let blobUrl = null;

    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/preingresso/${token}/logo`, {
          responseType: "blob",
        });
        if (cancelled) return;
        blobUrl = URL.createObjectURL(res.data);
        setLogoUrl(blobUrl);
      } catch {
        if (!cancelled) setLogoUrl(null);
      }
    })();

    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [struttura?.has_logo, token]);

  return (
    <header className="bg-white border-b border-slate-200 sticky top-0 z-20 shadow-sm">
      <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={struttura?.nome || "Logo struttura"}
              className="h-9 sm:h-10 max-w-[140px] object-contain shrink-0"
            />
          ) : (
            <StrutturaLogo className="w-9 h-9 sm:w-10 sm:h-10 shrink-0" />
          )}
          <div className="min-w-0">
            <p className="text-[10px] sm:text-xs font-bold tracking-widest text-slate-500 uppercase truncate">
              {struttura?.nome || "Struttura"}
            </p>
          </div>
        </div>
        {struttura?.telefono ? (
          <a
            href={`tel:${struttura.telefono}`}
            className="text-xs sm:text-sm text-emerald-700 flex items-center gap-1.5 shrink-0 hover:text-emerald-800 hover:underline text-right"
          >
            <Phone className="w-4 h-4 shrink-0" />
            <span className="hidden sm:inline">Hai bisogno di aiuto? Contatta la struttura</span>
            <span className="sm:hidden">Aiuto</span>
          </a>
        ) : (
          <span className="text-xs text-slate-400 hidden sm:inline">Hai bisogno di aiuto? Contatta la struttura</span>
        )}
      </div>
    </header>
  );
}

function StepBadge({ stepIndex }) {
  const step = STEPS[stepIndex];
  if (!step || stepIndex === 0) return null;
  return (
    <div className="bg-blue-600 text-white text-sm font-semibold px-4 py-2.5 rounded-t-xl flex items-center gap-2">
      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/20 text-xs font-bold">
        {stepIndex + 1}
      </span>
      {step.label}
    </div>
  );
}

function ProgressBlock({ progress, className = "" }) {
  return (
    <div className={className}>
      <div className="flex items-center justify-between text-xs text-slate-500 mb-2">
        <span className="font-medium">Avanzamento</span>
        <span className="font-semibold text-slate-700">{progress}% completato</span>
      </div>
      <Progress value={progress} className="h-2 bg-slate-100 [&>div]:bg-emerald-600" />
    </div>
  );
}

function ChecklistItem({ done, label, active }) {
  return (
    <li className="flex items-center gap-3 py-2">
      {done ? (
        <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
      ) : (
        <Circle className={`w-5 h-5 shrink-0 ${active ? "text-emerald-500" : "text-slate-300"}`} />
      )}
      <span
        className={`text-sm ${
          done ? "text-slate-800 font-medium" : active ? "text-emerald-700 font-medium" : "text-slate-500"
        }`}
      >
        {label}
      </span>
    </li>
  );
}

function ProgressChecklist({ data, allDocsReceived, currentStep, documentsOnly, therapyRequired, docsTotal = 0 }) {
  const items = [
    ...(!documentsOnly
      ? [
          { key: "contratto", done: data.checklist?.contratto_visto, label: "Visiona contratto" },
          { key: "firma", done: data.checklist?.contratto_firmato, label: "Firma contratto" },
        ]
      : []),
    ...(docsTotal > 0 || therapyRequired
      ? [{
          key: "documenti",
          done: allDocsReceived && docsTotal > 0,
          label: "Carica documenti richiesti",
        }]
      : []),
    ...(therapyRequired
      ? [{ key: "terapia", done: data.checklist?.terapia_ricevuta, label: "Carica terapia" }]
      : []),
    {
      key: "done",
      done: data.checklist?.submitted || data.checklist?.completata,
      label: documentsOnly ? "Invio completato" : "Pratica completata",
    },
  ];
  const activeIndex = Math.min(currentStep, items.length - 1);

  return (
    <ul className="divide-y divide-slate-100">
      {items.map((item, idx) => (
        <ChecklistItem key={item.key} done={item.done} label={item.label} active={idx === activeIndex && !item.done} />
      ))}
    </ul>
  );
}

function SecurityFooter({ className = "" }) {
  return (
    <p className={`flex items-center justify-center gap-1.5 text-xs text-slate-400 ${className}`}>
      <Lock className="w-3.5 h-3.5 shrink-0" />
      I tuoi dati sono al sicuro con crittografia SSL
    </p>
  );
}

function StepNav({ onBack, onNext, nextLabel = "Procedi", nextDisabled, nextLoading, showNext = true }) {
  return (
    <div className="flex flex-col-reverse sm:flex-row gap-3 pt-6 border-t border-slate-100 mt-6">
      {onBack && (
        <Button type="button" variant="outline" onClick={onBack} className="sm:w-auto w-full h-11 border-slate-300">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Indietro
        </Button>
      )}
      {showNext && (
        <Button
          type="button"
          className="flex-1 h-11 bg-emerald-600 hover:bg-emerald-700 text-base font-semibold"
          onClick={onNext}
          disabled={nextDisabled || nextLoading}
        >
          {nextLoading ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : null}
          {nextLabel}
          {!nextLoading && !["Conferma firma", "Conferma invio definitivo"].includes(nextLabel) ? (
            <ChevronRight className="w-4 h-4 ml-1" />
          ) : null}
        </Button>
      )}
    </div>
  );
}

function attachmentViewUrl(token, fileId) {
  if (!token || !fileId) return null;
  return `${API_URL}/api/preingresso/${token}/allegati/${fileId}`;
}

function AttachmentActions({ token, fileId, fileName, locked, uploading, onReplace, replaceCapture }) {
  const viewUrl = attachmentViewUrl(token, fileId);
  if (!fileId) return null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {viewUrl && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-9 bg-white border-slate-300 text-xs sm:text-sm"
          onClick={() => window.open(viewUrl, "_blank", "noopener,noreferrer")}
        >
          <Eye className="w-3.5 h-3.5 mr-1.5" />
          Visualizza
        </Button>
      )}
      {!locked && onReplace && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={uploading}
          onClick={() => onReplace(replaceCapture)}
          className="h-9 bg-white border-slate-300 text-xs sm:text-sm"
        >
          {uploading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
              Sostituisci
            </>
          )}
        </Button>
      )}
      {fileName && (
        <span className="text-xs text-slate-500 truncate max-w-[180px] sm:max-w-xs" title={fileName}>
          {fileName}
        </span>
      )}
    </div>
  );
}

function DocIcon({ docKey }) {
  const Icon = DOC_ICONS[docKey] || FileText;
  return (
    <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-slate-100 text-slate-500 shrink-0">
      <Icon className="w-4 h-4" />
    </span>
  );
}

export default function PreIngressoPortal() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [step, setStep] = useState(0);
  const [contrattoLetto, setContrattoLetto] = useState(false);
  const [luogo, setLuogo] = useState("");
  const [signing, setSigning] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [contrattoPdfUrl, setContrattoPdfUrl] = useState(null);
  const [contrattoLoading, setContrattoLoading] = useState(false);
  const [facilityImgError, setFacilityImgError] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const sigPadRef = useRef(null);
  const contrattoBlobRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/preingresso/${token}`);
      setData(res.data);
      const therapyReq = (res.data.requested_documents || []).some((d) => d.is_therapy);
      const docsTotal = (res.data.requested_documents || []).filter((d) => !d.is_therapy).length;
      const idx = statusToStepIndex(res.data.pre_admission_status, res.data.checklist, therapyReq, docsTotal);
      setStep(idx);
      setContrattoLetto(res.data.checklist?.contratto_visto || false);
    } catch (err) {
      toast.error(formatApiDetail(err.response?.data?.detail, "Link non valido o scaduto"));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (step !== 1 || !token) {
      if (contrattoBlobRef.current) {
        URL.revokeObjectURL(contrattoBlobRef.current);
        contrattoBlobRef.current = null;
      }
      setContrattoPdfUrl(null);
      return;
    }

    let cancelled = false;
    const fetchContratto = async () => {
      setContrattoLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/preingresso/${token}/contratto`, {
          responseType: "blob",
        });
        if (cancelled) return;
        if (contrattoBlobRef.current) {
          URL.revokeObjectURL(contrattoBlobRef.current);
        }
        const blobUrl = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        contrattoBlobRef.current = blobUrl;
        setContrattoPdfUrl(blobUrl);
      } catch (err) {
        if (!cancelled) {
          toast.error(formatApiDetail(err.response?.data?.detail, "Impossibile caricare il contratto"));
        }
      } finally {
        if (!cancelled) setContrattoLoading(false);
      }
    };

    fetchContratto();
    return () => {
      cancelled = true;
    };
  }, [step, token]);

  useEffect(
    () => () => {
      if (contrattoBlobRef.current) {
        URL.revokeObjectURL(contrattoBlobRef.current);
      }
    },
    []
  );

  const handleContrattoVisto = async () => {
    if (!contrattoLetto) {
      toast.error("Conferma di aver letto il contratto");
      return;
    }
    try {
      await axios.post(`${API_URL}/api/preingresso/${token}/contratto/visto`);
      await load();
      setStep(2);
    } catch (err) {
      toast.error(formatApiDetail(err.response?.data?.detail, "Errore"));
    }
  };

  const handleFirma = async () => {
    if (!luogo.trim()) {
      toast.error("Inserisci il luogo della firma");
      return;
    }
    if (sigPadRef.current?.isEmpty()) {
      toast.error("Disegna la firma");
      return;
    }
    setSigning(true);
    try {
      const firma = sigPadRef.current.toDataURL("image/png");
      await axios.post(`${API_URL}/api/preingresso/${token}/contratto/firma`, {
        firma_familiare: firma,
        luogo: luogo.trim(),
      });
      toast.success("Contratto firmato!");
      const res = await axios.get(`${API_URL}/api/preingresso/${token}`);
      setData(res.data);
      const therapyReq = (res.data.requested_documents || []).some((d) => d.is_therapy);
      const docsTotal = (res.data.requested_documents || []).filter((d) => !d.is_therapy).length;
      const idx = statusToStepIndex(res.data.pre_admission_status, res.data.checklist, therapyReq, docsTotal);
      setStep(idx);
      setContrattoLetto(res.data.checklist?.contratto_visto || false);
    } catch (err) {
      toast.error(formatApiDetail(err.response?.data?.detail, "Errore firma"));
    } finally {
      setSigning(false);
    }
  };

  const uploadDoc = async (docKey, file) => {
    if (!file) return;
    setUploading(docKey);
    const form = new FormData();
    form.append("file", file);
    try {
      await axios.post(`${API_URL}/api/preingresso/${token}/documenti/${docKey}`, form);
      toast.success("Documento caricato");
      await load();
    } catch (err) {
      toast.error(formatApiDetail(err.response?.data?.detail, "Errore upload"));
    } finally {
      setUploading(null);
    }
  };

  const uploadTerapia = async (file) => {
    if (!file) return;
    setUploading("terapia");
    const form = new FormData();
    form.append("file", file);
    try {
      await axios.post(`${API_URL}/api/preingresso/${token}/terapia`, form);
      toast.success("Terapia caricata");
      await load();
    } catch (err) {
      toast.error(formatApiDetail(err.response?.data?.detail, "Errore upload"));
    } finally {
      setUploading(null);
    }
  };

  const triggerFile = (key, capture) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = capture ? "image/*" : ".pdf,.jpg,.jpeg,.png,.heic,.heif";
    if (capture) input.capture = "environment";
    input.onchange = (e) => {
      const f = e.target.files?.[0];
      if (key === "terapia") uploadTerapia(f);
      else uploadDoc(key, f);
    };
    input.click();
  };

  const handleConfermaInvio = async () => {
    setConfirming(true);
    try {
      await axios.post(`${API_URL}/api/preingresso/${token}/conferma`);
      toast.success("Invio confermato");
      await load();
    } catch (err) {
      toast.error(formatApiDetail(err.response?.data?.detail, "Errore conferma invio"));
    } finally {
      setConfirming(false);
    }
  };

  const docsByGroup = (requested) => {
    const groups = {};
    (requested || [])
      .filter((d) => !d.is_therapy)
      .forEach((d) => {
        const g = d.group_label || "Documenti";
        if (!groups[g]) groups[g] = [];
        groups[g].push(d);
      });
    return groups;
  };

  const therapyRequired = (data?.requested_documents || []).some((d) => d.is_therapy);
  const docsTotal = (data?.requested_documents || []).filter((d) => !d.is_therapy).length;
  const allDocsReceived =
    (data?.checklist?.documenti_ricevuti || 0) >= (data?.checklist?.documenti_totali || 0);
  const documentsOnly = !!data?.documents_only;
  const submitted = !!data?.checklist?.submitted;
  const readyToSubmit = !!data?.checklist?.pronta_per_invio;

  const displayProgress = Math.max(data?.progress ?? 0, STEPS[step]?.progress ?? 10);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f0f2f5] flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#f0f2f5] flex items-center justify-center p-6 text-center">
        <div className="bg-white rounded-2xl border border-slate-200 p-8 max-w-md shadow-sm">
          <p className="text-slate-600">Link non valido o scaduto.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f0f2f5] flex flex-col">
      <PortalHeader struttura={data.struttura} token={token} />

      <main className="flex-1 max-w-5xl w-full mx-auto px-4 py-5 sm:py-8 pb-10">
        <div className="mb-5 sm:mb-8">
          <p className="text-xs font-bold text-emerald-600 uppercase tracking-wider">
            {documentsOnly ? "Richiesta documenti" : "Pratica di ingresso"}
          </p>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mt-1 tracking-tight">
            {data.ospite?.nome_completo}
          </h1>
        </div>

        {/* STEP 0 — Benvenuto (layout mockup a 2 colonne) */}
        {step === 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-5 sm:p-8 lg:p-10">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
                <div className="text-center lg:text-left">
                  <div className="relative w-36 h-36 sm:w-44 sm:h-44 mx-auto lg:mx-0 mb-6 rounded-full overflow-hidden border-4 border-white shadow-lg ring-2 ring-emerald-100 bg-emerald-50">
                    {!facilityImgError ? (
                      <img
                        src={FACILITY_PLACEHOLDER}
                        alt={data.struttura?.nome || "Struttura"}
                        className="w-full h-full object-cover"
                        onError={() => setFacilityImgError(true)}
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Building2 className="w-16 h-16 text-emerald-300" />
                      </div>
                    )}
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-3">Benvenuto</h2>
                  <p className="text-slate-600 text-sm sm:text-base leading-relaxed max-w-md mx-auto lg:mx-0">
                    {documentsOnly ? (
                      <>
                        La struttura <strong className="text-slate-800">{data.struttura?.nome}</strong> ha richiesto
                        la documentazione per{" "}
                        <strong className="text-slate-800">{data.ospite?.nome_completo}</strong>. Puoi caricare i
                        file richiesti in pochi minuti.
                      </>
                    ) : (
                      <>
                        Abbiamo predisposto la documentazione necessaria per l&apos;ingresso di{" "}
                        <strong className="text-slate-800">{data.ospite?.nome_completo}</strong> presso{" "}
                        <strong className="text-slate-800">{data.struttura?.nome}</strong>. La procedura richiede circa{" "}
                        <strong className="text-slate-800">5 minuti</strong>.
                      </>
                    )}
                  </p>
                </div>

                <div className="bg-slate-50 rounded-xl border border-slate-200 p-5 sm:p-6">
                  <p className="text-sm font-bold text-slate-800 mb-4">Il tuo avanzamento</p>
                  <ProgressBlock progress={displayProgress} className="mb-5" />
                  <ProgressChecklist
                    data={data}
                    allDocsReceived={allDocsReceived}
                    currentStep={1}
                    documentsOnly={documentsOnly}
                    therapyRequired={therapyRequired}
                    docsTotal={docsTotal}
                  />
                </div>
              </div>

              <div className="mt-8 sm:mt-10 max-w-md mx-auto lg:mx-0 lg:max-w-none">
                <Button
                  className="w-full lg:w-auto min-w-[200px] h-12 bg-emerald-600 hover:bg-emerald-700 text-base font-semibold px-8"
                  onClick={() => setStep(documentsOnly ? 3 : 1)}
                >
                  Inizia
                  <ChevronRight className="w-5 h-5 ml-1" />
                </Button>
                <SecurityFooter className="mt-5" />
              </div>
            </div>
          </div>
        )}

        {/* STEPS 1–5 — Card con badge step */}
        {step >= 1 && step <= 5 && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <StepBadge stepIndex={step} />

            <div className="p-5 sm:p-8">
              <ProgressBlock progress={displayProgress} className="mb-6 sm:mb-8" />

              {/* STEP 1 — Visiona contratto */}
              {step === 1 && (
                <>
                  <h2 className="text-lg sm:text-xl font-bold text-slate-900 mb-4">Visiona contratto</h2>
                  <div className="rounded-xl border border-slate-200 overflow-hidden bg-slate-100">
                    <div className="flex items-center justify-between gap-2 px-3 py-2 bg-slate-800 text-white text-xs sm:text-sm">
                      <span className="font-medium truncate">Contratto di ingresso</span>
                      <div className="flex items-center gap-1 shrink-0">
                        <span className="hidden sm:inline text-slate-300 mr-2">Anteprima PDF</span>
                        <button
                          type="button"
                          className="p-1.5 rounded hover:bg-white/10 opacity-60 cursor-default"
                          aria-hidden
                        >
                          <ZoomOut className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          className="p-1.5 rounded hover:bg-white/10 opacity-60 cursor-default"
                          aria-hidden
                        >
                          <ZoomIn className="w-3.5 h-3.5" />
                        </button>
                        {contrattoPdfUrl && (
                          <a
                            href={contrattoPdfUrl}
                            download="contratto.pdf"
                            className="p-1.5 rounded hover:bg-white/10"
                            title="Scarica PDF"
                          >
                            <Download className="w-3.5 h-3.5" />
                          </a>
                        )}
                      </div>
                    </div>
                    <div className="bg-white" style={{ minHeight: 320 }}>
                      {contrattoLoading ? (
                        <div className="flex items-center justify-center h-[50vh] max-h-[520px] min-h-[280px]">
                          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
                        </div>
                      ) : contrattoPdfUrl ? (
                        <object
                          data={contrattoPdfUrl}
                          type="application/pdf"
                          className="w-full h-[50vh] max-h-[520px] min-h-[280px] bg-white"
                          aria-label="Contratto di ingresso"
                        >
                          <iframe title="Contratto" src={contrattoPdfUrl} className="w-full h-[50vh] max-h-[520px] min-h-[280px]" />
                        </object>
                      ) : (
                        <div className="flex items-center justify-center h-[280px] text-sm text-slate-500 px-4 text-center">
                          Anteprima contratto non disponibile. Ricarica la pagina o contatta la struttura.
                        </div>
                      )}
                    </div>
                  </div>

                  <label className="flex items-start gap-3 mt-5 cursor-pointer select-none">
                    <Checkbox
                      checked={contrattoLetto}
                      onCheckedChange={(v) => setContrattoLetto(!!v)}
                      className="mt-0.5 border-slate-400 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
                    />
                    <span className="text-sm sm:text-base text-slate-700">Ho letto il contratto</span>
                  </label>

                  <StepNav
                    onBack={() => setStep(0)}
                    onNext={handleContrattoVisto}
                    nextDisabled={!contrattoLetto}
                  />
                  <SecurityFooter className="mt-4" />
                </>
              )}

              {/* STEP 2 — Firma contratto */}
              {step === 2 && (
                <>
                  <h2 className="text-lg sm:text-xl font-bold text-slate-900 mb-2">Firma contratto</h2>
                  <p className="text-sm text-slate-600 mb-5">
                    Firma qui sotto con il dito, mouse o stylus.
                  </p>

                  <div className="mb-4 max-w-xs">
                    <Label className="text-slate-600 text-sm">Luogo</Label>
                    <Input
                      value={luogo}
                      onChange={(e) => setLuogo(e.target.value)}
                      placeholder="Es. Roma"
                      className="mt-1.5 h-11"
                    />
                  </div>

                  <SignaturePadField
                    ref={sigPadRef}
                    backgroundColor="#ffffff"
                    canvasClassName="w-full h-44 sm:h-52 touch-none pl-10"
                    clearVariant="link"
                    clearClassName="text-sm text-slate-500 flex items-center gap-1.5 hover:text-emerald-700 transition-colors"
                    actionsClassName="flex flex-wrap items-center justify-between gap-2 mt-3"
                    decoration={
                      <>
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-red-400 font-serif text-xl pointer-events-none select-none">
                          ✕
                        </div>
                        <div className="absolute left-12 right-4 bottom-8 border-b border-dotted border-slate-300 pointer-events-none" />
                        <span className="absolute left-12 bottom-2 text-xs text-slate-400 pointer-events-none">Firma</span>
                      </>
                    }
                  />

                  <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-4 mt-5 text-sm text-emerald-900 flex gap-3">
                    <Lock className="w-4 h-4 shrink-0 mt-0.5 text-emerald-600" />
                    <span>Il contratto sarà firmato digitalmente e archiviato in modo sicuro.</span>
                  </div>

                  <StepNav
                    onBack={() => setStep(1)}
                    onNext={handleFirma}
                    nextLabel="Conferma firma"
                    nextLoading={signing}
                    nextDisabled={signing}
                  />
                </>
              )}

              {/* STEP 3 — Documenti */}
              {step === 3 && (
                <>
                  <h2 className="text-lg sm:text-xl font-bold text-slate-900 mb-1">Documenti richiesti</h2>
                  {docsTotal === 0 ? (
                    <p className="text-sm text-slate-500 mb-6">
                      Non sono richiesti documenti aggiuntivi. Puoi procedere al riepilogo.
                    </p>
                  ) : (
                    <p className="text-sm text-slate-500 mb-6">
                      Carica i documenti richiesti. Puoi allegare un file o scattare una foto.
                      {!submitted && " Potrai rivederli e sostituirli prima della conferma finale."}
                    </p>
                  )}

                  {Object.entries(docsByGroup(data.requested_documents)).map(([group, docs]) => (
                    <div key={group} className="mb-8 last:mb-0">
                      <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">{group}</h3>
                      <div className="space-y-2">
                        {docs.map((doc) => {
                          const received = doc.status === "received";
                          return (
                            <div
                              key={doc.key}
                              className={`flex flex-col gap-3 p-3 sm:p-4 rounded-xl border ${
                                received ? "border-emerald-200 bg-emerald-50/50" : "border-slate-200 bg-slate-50/80"
                              }`}
                            >
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                <DocIcon docKey={doc.key} />
                                <span className="text-sm font-medium text-slate-800 leading-snug">{doc.label}</span>
                                {received && (
                                  <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 ml-auto sm:ml-0" />
                                )}
                              </div>

                              {received ? (
                                <AttachmentActions
                                  token={token}
                                  fileId={doc.file_id}
                                  fileName={doc.file_name}
                                  locked={submitted}
                                  uploading={uploading === doc.key}
                                  onReplace={(capture) => triggerFile(doc.key, capture)}
                                  replaceCapture={false}
                                />
                              ) : (
                                <div className="flex flex-wrap items-center gap-2 pl-12 sm:pl-0">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    disabled={uploading === doc.key || submitted}
                                    onClick={() => triggerFile(doc.key, false)}
                                    className="h-9 bg-white border-slate-300 text-xs sm:text-sm"
                                  >
                                    {uploading === doc.key ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <>
                                        <Upload className="w-3.5 h-3.5 mr-1.5" />
                                        Carica file
                                      </>
                                    )}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    disabled={uploading === doc.key || submitted}
                                    onClick={() => triggerFile(doc.key, true)}
                                    className="h-9 bg-white border-slate-300 text-xs sm:text-sm"
                                  >
                                    <Camera className="w-3.5 h-3.5 mr-1.5" />
                                    Scatta foto
                                  </Button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}

                  <StepNav
                    onBack={data.checklist?.contratto_firmato ? null : () => setStep(2)}
                    onNext={() => setStep(therapyRequired ? 4 : 5)}
                    nextDisabled={!allDocsReceived}
                    nextLabel="Vai al riepilogo"
                  />

                  <p className="flex items-center gap-1.5 text-xs text-slate-400 mt-4">
                    <Lock className="w-3 h-3 shrink-0" />
                    I file sono protetti e visibili solo alla struttura.
                  </p>
                </>
              )}

              {/* STEP 4 — Terapia */}
              {step === 4 && (
                <>
                  <h2 className="text-lg sm:text-xl font-bold text-slate-900 mb-2">Terapia attuale dell&apos;ospite</h2>
                  <p className="text-sm text-slate-600 mb-6">
                    Caricare una foto o PDF della terapia firmata dal medico.
                  </p>

                  {data.checklist?.terapia_ricevuta ? (
                    <div className="mb-6">
                      <AttachmentActions
                        token={token}
                        fileId={data.therapy_file?.file_id}
                        fileName={data.therapy_file?.file_name}
                        locked={submitted}
                        uploading={uploading === "terapia"}
                        onReplace={(capture) => triggerFile("terapia", capture)}
                        replaceCapture={false}
                      />
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-slate-300 rounded-2xl p-8 sm:p-12 text-center bg-slate-50/80 mb-6">
                      <CloudUpload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                      <p className="text-sm text-slate-500 mb-1">Trascina qui il file oppure usa i pulsanti</p>
                      <p className="text-xs text-slate-400 mb-6">PDF, JPG, PNG — max 10 MB</p>
                      <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-sm mx-auto">
                        <Button
                          variant="outline"
                          disabled={uploading === "terapia" || submitted}
                          onClick={() => triggerFile("terapia", false)}
                          className="h-11 bg-white border-slate-300 flex-1"
                        >
                          {uploading === "terapia" ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <Upload className="w-4 h-4 mr-2" />
                          )}
                          Carica PDF
                        </Button>
                        <Button
                          variant="outline"
                          disabled={uploading === "terapia" || submitted}
                          onClick={() => triggerFile("terapia", true)}
                          className="h-11 bg-white border-slate-300 flex-1"
                        >
                          <Camera className="w-4 h-4 mr-2" />
                          Scatta foto
                        </Button>
                      </div>
                    </div>
                  )}

                  <div className="rounded-xl bg-amber-50 border border-amber-100 p-4 text-sm text-amber-900">
                    La terapia sarà verificata dal nostro team sanitario.
                  </div>

                  <StepNav
                    onBack={() => setStep(3)}
                    onNext={() => setStep(5)}
                    nextDisabled={!data.checklist?.terapia_ricevuta}
                    nextLabel="Vai al riepilogo"
                  />
                </>
              )}

              {/* STEP 5 — Riepilogo / Conferma */}
              {step === 5 && (
                submitted ? (
                <div className="text-center py-2 sm:py-4">
                  <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5 ring-4 ring-emerald-50">
                    <Check className="w-10 h-10 text-emerald-600 stroke-[3]" />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2">Grazie!</h2>
                  <p className="text-slate-600 mb-8 text-sm sm:text-base">
                    {documentsOnly
                      ? "I documenti sono stati inviati correttamente."
                      : "La tua pratica è stata completata correttamente."}
                  </p>

                  <div className="bg-slate-50 rounded-xl border border-slate-200 p-5 sm:p-6 text-left max-w-md mx-auto mb-6 space-y-3">
                    {!documentsOnly && (
                      <div className="flex items-center justify-between gap-3 text-sm">
                        <span className="text-slate-600">Contratto</span>
                        <span className="font-semibold text-emerald-700 flex items-center gap-1.5">
                          <CheckCircle2 className="w-4 h-4" />
                          Firmato
                        </span>
                      </div>
                    )}
                    <div className="flex items-center justify-between gap-3 text-sm">
                      <span className="text-slate-600">Documenti</span>
                      <span className="font-semibold text-emerald-700 flex items-center gap-1.5">
                        <CheckCircle2 className="w-4 h-4" />
                        {docsTotal === 0
                          ? "Nessun documento richiesto"
                          : `${data.checklist?.documenti_ricevuti} di ${data.checklist?.documenti_totali} ricevuti`}
                      </span>
                    </div>
                    {therapyRequired && (
                      <div className="flex items-center justify-between gap-3 text-sm">
                        <span className="text-slate-600">Terapia</span>
                        <span className="font-semibold text-emerald-700 flex items-center gap-1.5">
                          <CheckCircle2 className="w-4 h-4" />
                          {data.checklist?.terapia_ricevuta ? "Ricevuta" : "Non ricevuta"}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="max-w-md mx-auto space-y-3 text-left">
                    {(data.requested_documents || [])
                      .filter((doc) => !doc.is_therapy && doc.file_id)
                      .map((doc) => (
                        <div key={doc.key} className="flex items-center justify-between gap-3 rounded-lg border bg-slate-50 p-3 text-sm">
                          <span className="text-slate-700 truncate">{doc.label}</span>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="shrink-0 h-8"
                            onClick={() => window.open(attachmentViewUrl(token, doc.file_id), "_blank", "noopener,noreferrer")}
                          >
                            <Eye className="w-3.5 h-3.5 mr-1" />
                            Visualizza
                          </Button>
                        </div>
                      ))}
                    {therapyRequired && data.therapy_file?.file_id && (
                      <div className="flex items-center justify-between gap-3 rounded-lg border bg-slate-50 p-3 text-sm">
                        <span className="text-slate-700 truncate">Terapia</span>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="shrink-0 h-8"
                          onClick={() => window.open(attachmentViewUrl(token, data.therapy_file.file_id), "_blank", "noopener,noreferrer")}
                        >
                          <Eye className="w-3.5 h-3.5 mr-1" />
                          Visualizza
                        </Button>
                      </div>
                    )}
                  </div>

                  <SecurityFooter className="mt-8" />
                </div>
                ) : (
                <div className="py-2 sm:py-4">
                  <h2 className="text-lg sm:text-xl font-bold text-slate-900 mb-2">Riepilogo e conferma invio</h2>
                  <p className="text-sm text-slate-600 mb-6">
                    Verifica i file allegati. Puoi visualizzarli o sostituirli prima di confermare l&apos;invio definitivo.
                  </p>

                  <div className="space-y-3 mb-6">
                    {(data.requested_documents || [])
                      .filter((doc) => !doc.is_therapy)
                      .map((doc) => (
                        <div key={doc.key} className="rounded-xl border border-slate-200 bg-slate-50/80 p-4">
                          <p className="text-sm font-medium text-slate-900 mb-3">{doc.label}</p>
                          {doc.file_id ? (
                            <AttachmentActions
                              token={token}
                              fileId={doc.file_id}
                              fileName={doc.file_name}
                              locked={false}
                              uploading={uploading === doc.key}
                              onReplace={(capture) => triggerFile(doc.key, capture)}
                              replaceCapture={false}
                            />
                          ) : (
                            <p className="text-xs text-amber-700">Documento mancante</p>
                          )}
                        </div>
                      ))}
                    {therapyRequired && (
                      <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4">
                        <p className="text-sm font-medium text-slate-900 mb-3">Terapia firmata dal medico</p>
                        {data.therapy_file?.file_id ? (
                          <AttachmentActions
                            token={token}
                            fileId={data.therapy_file.file_id}
                            fileName={data.therapy_file.file_name}
                            locked={false}
                            uploading={uploading === "terapia"}
                            onReplace={(capture) => triggerFile("terapia", capture)}
                            replaceCapture={false}
                          />
                        ) : (
                          <p className="text-xs text-amber-700">Terapia mancante</p>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="rounded-xl bg-amber-50 border border-amber-100 p-4 text-sm text-amber-900 mb-6">
                    Dopo la conferma non sarà più possibile modificare i file allegati.
                  </div>

                  <StepNav
                    onBack={() => setStep(therapyRequired ? 4 : 3)}
                    onNext={handleConfermaInvio}
                    nextLabel="Conferma invio definitivo"
                    nextLoading={confirming}
                    nextDisabled={!readyToSubmit || confirming}
                  />
                  <SecurityFooter className="mt-4" />
                </div>
                )
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
